import { useState, memo } from "react";
import { Bot, User, ThumbsUp, ThumbsDown } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import posthog from "@/lib/posthog";
import { Button } from "@/components/ui/button";
import type { FeedbackType, DislikeReason } from "@/services/ai.service";

// Stable references so ReactMarkdown doesn't re-parse on every render
const remarkPlugins = [remarkGfm];
const rehypePlugins = [rehypeSanitize];
const markdownComponents = {
  p: ({ children }: { children?: React.ReactNode }) => <p className="mb-2 last:mb-0">{children}</p>,
  strong: ({ children }: { children?: React.ReactNode }) => <strong className="font-semibold">{children}</strong>,
  ul: ({ children }: { children?: React.ReactNode }) => <ul className="list-disc pl-4 mb-2">{children}</ul>,
  ol: ({ children }: { children?: React.ReactNode }) => <ol className="list-decimal pl-4 mb-2">{children}</ol>,
  li: ({ children }: { children?: React.ReactNode }) => <li className="mb-1">{children}</li>,
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="overflow-x-auto my-4">
      <table className="min-w-full divide-y divide-border border border-border rounded-lg">{children}</table>
    </div>
  ),
  thead: ({ children }: { children?: React.ReactNode }) => <thead className="bg-muted/50">{children}</thead>,
  tbody: ({ children }: { children?: React.ReactNode }) => <tbody className="divide-y divide-border bg-card">{children}</tbody>,
  tr: ({ children }: { children?: React.ReactNode }) => <tr className="hover:bg-muted/20 transition-colors">{children}</tr>,
  th: ({ children }: { children?: React.ReactNode }) => (
    <th className="px-4 py-2 text-left text-xs font-semibold text-foreground uppercase tracking-wider">{children}</th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => <td className="px-4 py-2 text-sm text-foreground break-words">{children}</td>,
};

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
}

interface ChatMessageProps {
  message: Message;
  onFeedback?: (feedback: FeedbackType, dislikeReason?: DislikeReason, customReason?: string, regenerate?: boolean) => void;
  isStreaming?: boolean;
}

const DISLIKE_REASONS: { value: DislikeReason; label: string }[] = [
  { value: "incorrect", label: "Incorrect" },
  { value: "too_generic", label: "Too generic" },
  { value: "not_actionable", label: "Not actionable" },
  { value: "too_long", label: "Too long" },
  { value: "other", label: "Other" },
];

const ChatMessageInner = ({ message, onFeedback, isStreaming }: ChatMessageProps) => {
  const [feedbackState, setFeedbackState] = useState<"none" | "liked" | "disliked">("none");
  const [showDislikePanel, setShowDislikePanel] = useState(false);
  const [selectedReason, setSelectedReason] = useState<DislikeReason | null>(null);
  const [customReason, setCustomReason] = useState("");
  const [shouldRegenerate, setShouldRegenerate] = useState(false);

  const handleLike = () => {
    setFeedbackState("liked");
    setShowDislikePanel(false);
    posthog.capture("ai_response_liked", { message_id: message.id });
    onFeedback?.("like");
  };

  const handleDislikeClick = () => {
    const opening = !showDislikePanel;
    setShowDislikePanel(opening);
    if (opening) posthog.capture("ai_dislike_panel_opened", { message_id: message.id });
  };

  const handleDislikeSubmit = () => {
    if (!selectedReason) return;
    setFeedbackState("disliked");
    setShowDislikePanel(false);
    posthog.capture("ai_response_disliked", {
      message_id: message.id,
      reason: selectedReason,
      regenerate: shouldRegenerate,
    });
    onFeedback?.(
      "dislike",
      selectedReason,
      selectedReason === "other" ? customReason : undefined,
      shouldRegenerate
    );
  };

  const showFeedback = message.role === "assistant" && !isStreaming && onFeedback;

  return (
    <div className={`flex gap-3 ${message.role === "user" ? "justify-end" : "justify-start"}`}>
      {message.role === "assistant" && (
        <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
          <Bot className="w-5 h-5 text-primary-foreground" />
        </div>
      )}
      <div className={`${message.role === "user" ? "max-w-[80%]" : "max-w-[90%]"} flex flex-col gap-1`}>
        <div
          className={`rounded-lg px-4 py-3 break-words ${
            message.role === "user"
              ? "bg-primary text-primary-foreground"
              : "bg-secondary text-secondary-foreground"
          }`}
        >
          {message.role === "assistant" ? (
            <div className="text-sm prose prose-sm prose-slate dark:prose-invert max-w-none">
              <ReactMarkdown
                remarkPlugins={remarkPlugins}
                rehypePlugins={rehypePlugins}
                components={markdownComponents}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          ) : (
            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
          )}
        </div>

        {showFeedback && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1 pl-1">
              <button
                onClick={handleLike}
                disabled={false}
                className={`p-1 rounded transition-colors ${
                  feedbackState === "liked"
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground"
                } disabled:opacity-50`}
                title="Helpful"
              >
                <ThumbsUp className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={handleDislikeClick}
                disabled={false}
                className={`p-1 rounded transition-colors ${
                  feedbackState === "disliked"
                    ? "text-destructive"
                    : "text-muted-foreground hover:text-foreground"
                } disabled:opacity-50`}
                title="Not helpful"
              >
                <ThumbsDown className="w-3.5 h-3.5" />
              </button>
            </div>

            {showDislikePanel && (
              <div className="bg-card border border-border rounded-lg p-3 flex flex-col gap-3 text-sm">
                <p className="font-medium text-foreground">What went wrong?</p>
                <div className="flex flex-wrap gap-2">
                  {DISLIKE_REASONS.map((r) => (
                    <button
                      key={r.value}
                      onClick={() => setSelectedReason(r.value)}
                      className={`px-3 py-1 rounded-full border text-xs transition-colors ${
                        selectedReason === r.value
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
                      }`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>

                {selectedReason === "other" && (
                  <input
                    type="text"
                    placeholder="Describe the issue..."
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    className="w-full px-3 py-1.5 text-xs rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                )}

                <label className="flex items-center gap-2 cursor-pointer text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={shouldRegenerate}
                    onChange={(e) => setShouldRegenerate(e.target.checked)}
                    className="rounded"
                  />
                  Regenerate answer
                </label>

                <div className="flex justify-end">
                  <Button
                    size="sm"
                    variant="default"
                    disabled={!selectedReason || (selectedReason === "other" && !customReason.trim())}
                    onClick={handleDislikeSubmit}
                    className="h-7 text-xs"
                  >
                    Submit
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {message.role === "user" && (
        <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
          <User className="w-5 h-5 text-primary-foreground" />
        </div>
      )}
    </div>
  );
};

// Only re-render when content or streaming state changes — onFeedback reference churn during streaming is intentionally ignored
export const ChatMessage = memo(ChatMessageInner, (prev, next) =>
  prev.message.id === next.message.id &&
  prev.message.content === next.message.content &&
  prev.isStreaming === next.isStreaming
);
